import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// Numero di pagine con ancora firma OTP (vedi ConsulenzaPDF.tsx): deve coincidere
// con i F(n) del documento, altrimenti OTP risponde 422.
export const BONIFICA_FOOTER_PAGES = 5;

const C = {
    black:         '#1a1a1a',
    darkGray:      '#333333',
    midGray:       '#666666',
    silver:        '#cccccc',
    accent:        '#8b0000',
    sigBorder:     '#d32f2f', // bordo rosso dei riquadri firma (come MandatoPDF/InformativaPrivacyPDF)
    headerBg:      '#1c2b4a',
    sectionBg:     '#dde3ec',
    sectionBorder: '#1c2b4a',
    fieldLine:     '#888888',
    white:         '#ffffff',
};

const styles = StyleSheet.create({
    page: {
        fontSize: 9.5,
        fontFamily: 'Times-Roman',
        lineHeight: 1.5,
        color: C.black,
    },

    /* Header */
    header:          { paddingTop: 0, paddingBottom: 4, paddingHorizontal: 35 },
    headerBanner:    { width: '100%', height: 75, objectFit: 'contain' },
    headerInner:     { flexDirection: 'row', alignItems: 'center', paddingTop: 10, paddingBottom: 6 },
    logo:            { width: 55, height: 45, objectFit: 'contain' },
    headerTextBlock: { flex: 1, paddingLeft: 10 },
    headerAgency:    { fontSize: 12, fontFamily: 'Times-Roman', color: C.black, letterSpacing: 0.3 },
    headerSub:       { fontSize: 7.5, color: C.midGray, marginTop: 1 },
    headerAccentBar: { height: 2, backgroundColor: C.accent },
    headerMeta:      { flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 35, paddingTop: 3, paddingBottom: 3, borderTopWidth: 0.5, borderTopColor: C.silver },
    headerPageNum:   { fontSize: 7, color: C.midGray, textAlign: 'right' },

    /* Footer */
    footer:               { marginTop: 'auto', borderTopWidth: 0.5, borderTopColor: C.silver, paddingHorizontal: 35, paddingVertical: 6, flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' },
    footerCopyright:      { fontSize: 6, color: C.midGray, fontStyle: 'italic', flex: 1 },
    footerSignature:      { flexDirection: 'column', alignItems: 'flex-start', marginLeft: 16, minWidth: 130 },
    footerSignatureLabel: { fontSize: 7, color: C.midGray, marginBottom: 3 },
    footerSignatureLine:  { width: 130, height: 26, borderWidth: 0.75, borderColor: C.sigBorder, backgroundColor: '#fafafa' },

    /* Titolo */
    docTitleBar: {
        borderWidth: 1,
        borderColor: C.sectionBorder,
        paddingVertical: 6,
        paddingHorizontal: 12,
        marginBottom: 10,
        marginTop: 4,
    },
    docTitleMain: {
        fontSize: 12,
        fontFamily: 'Times-Roman',
        textAlign: 'center',
        color: C.headerBg,
        letterSpacing: 0.5,
    },

    /* Sezione titolo */
    sectionTitle: {
        backgroundColor: C.sectionBg,
        borderLeftWidth: 3,
        borderLeftColor: C.sectionBorder,
        paddingVertical: 4,
        paddingHorizontal: 8,
        marginTop: 10,
        marginBottom: 6,
        fontSize: 9,
        fontFamily: 'Times-Roman',
        color: C.headerBg,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
    },
    subSectionTitle: {
        fontSize: 9.5,
        fontFamily: 'Times-Roman',
        color: C.headerBg,
        marginTop: 6,
        marginBottom: 4,
        fontStyle: 'italic',
    },

    /* Fields */
    fieldRow:       { flexDirection: 'row', alignItems: 'flex-end', marginBottom: 5 },
    fieldLabel:     { fontFamily: 'Times-Roman', fontSize: 9, color: C.darkGray, marginRight: 5, whiteSpace: 'nowrap' },
    fieldValue:     { flex: 1, borderBottomWidth: 0.75, borderBottomColor: C.fieldLine, fontSize: 9.5, paddingBottom: 2, minHeight: 14, color: C.black },
    fieldLabelBold: { fontFamily: 'Times-Roman', fontSize: 10, color: C.black, marginBottom: 2 },

    /* Checkbox */
    checkRow:     { flexDirection: 'row', alignItems: 'center', marginBottom: 3 },
    checkbox:     { width: 11, height: 11, borderWidth: 1, borderColor: C.darkGray, borderRadius: 1, marginRight: 5, alignItems: 'center', justifyContent: 'center', backgroundColor: C.white },
    checkboxTick: { fontSize: 8, color: C.accent, lineHeight: 1 },
    checkLabel:   { fontSize: 9.5, color: C.black },

    /* Text box */
    textBox:        { borderWidth: 0.75, borderColor: C.silver, backgroundColor: '#fafafa', minHeight: 60, marginBottom: 6, padding: 5 },
    textBoxContent: { fontSize: 9.5, color: C.black },

    /* Paragrafi */
    para: { marginBottom: 5, textAlign: 'justify', fontSize: 9.5, lineHeight: 1.5 },

    /* Compenso */
    compensoBox: {
        borderWidth: 1,
        borderColor: C.fieldLine,
        paddingHorizontal: 12,
        paddingVertical: 8,
        minWidth: 160,
        minHeight: 30,
        alignSelf: 'flex-start',
        marginBottom: 6,
        marginTop: 2,
    },

    /* Firma */
    signatureSection:  { marginTop: 4, marginBottom: 4 },
    signatureLabel:    { fontSize: 10, fontStyle: 'italic', color: C.midGray, textAlign: 'left', marginBottom: 4 },
    signatureArrowRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
    signatureArrow:    { fontSize: 13, color: C.accent, marginRight: 6 },
    signatureLine:     { flex: 1, borderWidth: 0.75, borderColor: C.sigBorder, backgroundColor: '#fafafa', height: 30 },

    divider: { borderTopWidth: 0.5, borderTopColor: C.silver, marginVertical: 6 },
});

/* Helpers */

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Text style={styles.sectionTitle}>{children}</Text>
);

const SubSectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Text style={styles.subSectionTitle}>{children}</Text>
);

const Para = ({ children, bold, style }: any) => (
    <Text style={[styles.para, bold ? { fontFamily: 'Times-Roman' } : {}, style]}>
        {children}
    </Text>
);

const B = ({ children }: { children: any }) => (
    <Text style={{ fontFamily: 'Times-Roman' }}>{children}</Text>
);

const BulletItem = ({ children }: { children: any }) => (
    <View style={{ flexDirection: 'row', marginBottom: 4, paddingLeft: 6 }}>
        <Text style={{ fontSize: 9.5, marginRight: 6, lineHeight: 1.5 }}>-</Text>
        <Text style={{ flex: 1, fontSize: 9.5, lineHeight: 1.5, textAlign: 'justify' }}>{children}</Text>
    </View>
);

const isSupportedImageUrl = (url: string | undefined): boolean =>
    !!url && !url.startsWith('data:image/svg');

/* Header */
const PageHeader = ({ settings, data }: any) => {
    const bannerUrl  = settings?.mandatoHeaderUrl || settings?.mandatoHeaderImageUrl || settings?.reportHeaderUrl;
    const logoUrl    = settings?.logoUrl || data?.logoUrl;
    const agencyName = settings?.agencyName || data?.nome_azienda || '';
    return (
        <View style={styles.header}>
            {isSupportedImageUrl(bannerUrl) ? (
                <Image src={bannerUrl} style={styles.headerBanner} />
            ) : (
                <View style={styles.headerInner}>
                    {isSupportedImageUrl(logoUrl) && <Image src={logoUrl} style={styles.logo} />}
                    <View style={styles.headerTextBlock}>
                        <Text style={styles.headerAgency}>{agencyName}</Text>
                        <Text style={styles.headerSub}>Investigazioni Private · Sicurezza · Consulenza</Text>
                    </View>
                </View>
            )}
            <View style={styles.headerAccentBar} />
            <View style={styles.headerMeta}>
                <Text style={styles.headerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />
            </View>
        </View>
    );
};

/* Footer */
const PageFooter = ({ pageNum }: { pageNum: number }) => (
    <View style={styles.footer}>
        <Text style={styles.footerCopyright}>
            Il presente documento costituisce opera dell'ingegno ai sensi della normativa vigente ed è riservato all'uso esclusivo dei soggetti espressamente autorizzati dall'autore.
        </Text>
        <View style={styles.footerSignature}>
            <Text style={styles.footerSignatureLabel}>Firma Mandante:</Text>
            <View style={styles.footerSignatureLine}>
                <Text style={{ fontSize: 0.01, color: '#ffffff' }}>{`#otp_bf${pageNum}#`}</Text>
            </View>
        </View>
    </View>
);

/* Documento */

export const BonificaPDF = ({ data }: { data: any }) => {
    const { cliente, pratica, settings } = data;
    // Modulo in bianco da compilare a penna: nessuna casella spuntata in automatico.
    const inBianco = !!data.inBianco;
    const safeSettings = settings || {};
    const nomeAzienda  = safeSettings?.agencyName || data.nome_azienda || '';

    console.group('[BonificaPDF] dati ricevuti');
    console.log('data (intero):', data);
    console.log('cliente:', cliente);
    console.log('pratica:', pratica);
    console.log('settings (raw):', settings);
    console.log('nome_azienda:', nomeAzienda);
    console.log('--- immagini settings ---');
    console.log('logoUrl:',               safeSettings?.logoUrl);
    console.log('mandatoHeaderUrl:',      safeSettings?.mandatoHeaderUrl);
    console.log('reportHeaderUrl:',       safeSettings?.reportHeaderUrl);
    console.log('mandatoFooterUrl:',      safeSettings?.mandatoFooterUrl);
    console.log('privacyPolicyUrl:',      safeSettings?.privacyPolicyUrl);
    console.groupEnd();

    const isAzienda    = cliente?.tipo === 'Azienda';
    // Ripiego per le aziende registrate col vecchio modello (denominazioneTerzi / nome).
    const nomeMandante = isAzienda
        ? (cliente?.ragioneSociale || cliente?.denominazioneTerzi || cliente?.nome || '')
        : `${cliente?.nome || ''} ${cliente?.surname || ''}`.trim();

    const pageStyle    = styles.page;
    const contentStyle = { paddingHorizontal: 35 };

    const H = () => <PageHeader settings={safeSettings} data={data} />;
    const F = (n: number) => <PageFooter pageNum={n} />;

    const rifStr = pratica?.rif_numero && !pratica.rif_numero.toLowerCase().startsWith('temp')
        ? pratica.rif_numero : '';

    return (
        <Document>

            {/* Pagina 1 */}
            <Page size="A4" style={pageStyle}>
                {H()}
                <View style={contentStyle}>

                    {/* Titolo */}
                    <View style={styles.docTitleBar}>
                        <Text style={styles.docTitleMain}>
                            INCARICO PER LO SVOLGIMENTO DI ATTIVITA' DI BONIFICA ELETTRONICA
                        </Text>
                    </View>

                    {/* Data + Rif */}
                    <View style={styles.fieldRow}>
                        <Text style={[styles.fieldLabelBold, { width: 120 }]}>Data Sottoscrizione</Text>
                        <Text style={[styles.fieldValue, { maxWidth: 120 }]}>{pratica?.data_incarico || ''}</Text>
                        {rifStr ? (
                            <>
                                <Text style={[styles.fieldLabel, { marginLeft: 20 }]}>Rif. N°</Text>
                                <Text style={[styles.fieldValue, { maxWidth: 80 }]}>{rifStr}</Text>
                            </>
                        ) : null}
                    </View>

                    {/* Il/la Sottoscritto/a */}
                    <View style={styles.fieldRow}>
                        <Text style={[styles.fieldLabelBold, { width: 120 }]}>Il/la Sottoscritto/a</Text>
                        <Text style={styles.fieldValue}>{nomeMandante}</Text>
                    </View>

                    {isAzienda ? (
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>Partita IVA</Text>
                            <Text style={[styles.fieldValue, { flex: 1 }]}>{cliente?.partitaIva || ''}</Text>
                            <Text style={[styles.fieldLabel, { marginLeft: 10 }]}>Cod. SDI</Text>
                            <Text style={[styles.fieldValue, { flex: 0.6 }]}>{cliente?.codiceSdi || ''}</Text>
                        </View>
                    ) : (
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>nato/a a</Text>
                            <Text style={[styles.fieldValue, { flex: 2 }]}>{cliente?.luogoNascita || ''}</Text>
                            <Text style={[styles.fieldLabel, { marginLeft: 10 }]}>in data</Text>
                            <Text style={[styles.fieldValue, { flex: 1 }]}>{cliente?.dataNascita || ''}</Text>
                        </View>
                    )}

                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>residente in via</Text>
                        <Text style={[styles.fieldValue, { flex: 2 }]}>{cliente?.indirizzo || ''}</Text>
                        <Text style={[styles.fieldLabel, { marginLeft: 10 }]}>C.A.P.</Text>
                        <Text style={[styles.fieldValue, { flex: 0.7 }]}>{cliente?.cap || ''}</Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>Comune:</Text>
                        <Text style={[styles.fieldValue, { flex: 2 }]}>{cliente?.citta || ''}</Text>
                        <Text style={[styles.fieldLabel, { marginLeft: 10 }]}>Prov.</Text>
                        <Text style={[styles.fieldValue, { flex: 0.5 }]}>{cliente?.provincia || ''}</Text>
                    </View>

                    {!isAzienda && (
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>Codice Fiscale:</Text>
                            <Text style={styles.fieldValue}>{cliente?.codiceFiscale || ''}</Text>
                        </View>
                    )}

                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>Identificato tramite documento</Text>
                        <Text style={[styles.fieldValue, { flex: 1.5 }]}>{cliente?.docTipo || ''}</Text>
                        <Text style={[styles.fieldLabel, { marginLeft: 10 }]}>n°</Text>
                        <Text style={[styles.fieldValue, { flex: 1 }]}>{cliente?.docNumero || ''}</Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>rilasciato il</Text>
                        <Text style={[styles.fieldValue, { flex: 1 }]}>{cliente?.docDataRilascio || ''}</Text>
                        <Text style={[styles.fieldLabel, { marginLeft: 10 }]}>da</Text>
                        <Text style={[styles.fieldValue, { flex: 2 }]}>{cliente?.docRilascioDa || ''}</Text>
                    </View>

                    {/* Agisce per conto */}
                    <Text style={[styles.fieldLabelBold, { marginTop: 6, marginBottom: 4 }]}>
                        che agisce in nome e per conto
                    </Text>
                    {/* Per un'azienda il referente firma per conto di questa: "di" riporta la ragione
                        sociale e sede/CAP/PIVA vengono dai campi nativi. Per un privato si usa il blocco terzi. */}
                    <View style={styles.checkRow}>
                        <View style={styles.checkbox}>
                            {!inBianco && !isAzienda && !cliente?.perContoDiTerzi && <View style={{ width: 7, height: 7, backgroundColor: C.accent }} />}
                        </View>
                        <Text style={[styles.checkLabel, { fontFamily: 'Times-Roman' }]}>PROPRIO</Text>
                    </View>
                    <View style={[styles.fieldRow, { alignItems: 'center', marginTop: 2 }]}>
                        <View style={styles.checkbox}>
                            {(isAzienda || cliente?.perContoDiTerzi) && <View style={{ width: 7, height: 7, backgroundColor: C.accent }} />}
                        </View>
                        <Text style={[styles.checkLabel, { marginRight: 6 }]}>di</Text>
                        <Text style={styles.fieldValue}>{
                            isAzienda
                                ? (cliente?.ragioneSociale || cliente?.denominazioneTerzi || cliente?.nome || '')
                                : (cliente?.denominazioneTerzi || '')
                        }</Text>
                    </View>

                    {(isAzienda || cliente?.perContoDiTerzi) ? (
                        <>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>con sede in via</Text>
                                <Text style={[styles.fieldValue, { flex: 2 }]}>{
                                    isAzienda ? (cliente?.indirizzo || cliente?.sedeTerzi || '') : (cliente?.sedeTerzi || '')
                                }</Text>
                                <Text style={[styles.fieldLabel, { marginLeft: 10 }]}>C.A.P.</Text>
                                <Text style={[styles.fieldValue, { flex: 0.7 }]}>{
                                    isAzienda ? (cliente?.cap || cliente?.capTerzi || '') : (cliente?.capTerzi || '')
                                }</Text>
                            </View>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>Comune:</Text>
                                <Text style={[styles.fieldValue, { flex: 2 }]}>{
                                    isAzienda ? (cliente?.citta || cliente?.comuneTerzi || '') : (cliente?.comuneTerzi || '')
                                }</Text>
                                <Text style={[styles.fieldLabel, { marginLeft: 10 }]}>Prov.</Text>
                                <Text style={[styles.fieldValue, { flex: 0.5 }]}>{
                                    isAzienda ? (cliente?.provincia || cliente?.provinciaTerzi || '') : (cliente?.provinciaTerzi || '')
                                }</Text>
                            </View>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>p. IVA/codice fiscale</Text>
                                <Text style={styles.fieldValue}>{
                                    isAzienda ? (cliente?.partitaIva || cliente?.pivaOrCfTerzi || '') : (cliente?.pivaOrCfTerzi || '')
                                }</Text>
                            </View>
                            <Para style={{ fontSize: 8.5, fontStyle: 'italic', color: C.midGray }}>
                                che rappresenta ai fini del presente mandato in qualità di Titolare, Legale Rappresentante o persona da questi all'uopo espressamente delegata, procuratore con delega del C.d.A., con pieni poteri per il presente atto,
                            </Para>
                        </>
                    ) : null}

                    <Para style={{ marginTop: 6 }}>
                        di seguito denominato <B>"Mandante"</B>,
                    </Para>

                    <SectionTitle>PREMESSE E FINALITÀ DELL'INCARICO</SectionTitle>

                    <Para>
                        Il Mandante, in relazione ai beni e ai luoghi indicati nel presente mandato, premette e dichiara quanto segue:
                    </Para>

                    <BulletItem>
                        di intendere <B>tutelare, difendere o far valere un proprio diritto</B>, anche in sede giudiziaria e/o stragiudiziale, mediante il conferimento di un incarico professionale avente ad oggetto attività tecniche di accertamento;
                    </BulletItem>
                    <BulletItem>
                        di avere il <B>concreto sospetto</B> di essere soggetto a indebite attività di intercettazione ambientale, telefonica, telematica e/o di captazione, localizzazione, trasmissione, registrazione o sottrazione di informazioni e dati, in violazione della propria riservatezza e della propria sfera personale e/o professionale;
                    </BulletItem>
                    <BulletItem>
                        di essere consapevole che l'Agenzia è soggetto professionalmente qualificato e dotato delle competenze tecniche necessarie allo svolgimento delle attività richieste;
                    </BulletItem>
                    <BulletItem>
                        di richiedere lo svolgimento di <B>attività tecnica di bonifica elettronica, ambientale, telefonica, telematica e informatica</B>, finalizzata alla ricerca, individuazione, identificazione ed eventuale classificazione di dispositivi, apparati, sistemi o strumenti idonei alla captazione, intercettazione, localizzazione, registrazione, trasmissione o sottrazione di informazioni e dati;
                    </BulletItem>
                    <BulletItem>
                        di prendere atto che le attività oggetto del presente mandato <B>hanno natura di accertamento tecnico, consulenziale e di preventiva tutela difensiva</B>, e <B>non costituiscono attività di intercettazione, captazione, controllo occulto o altra attività vietata dalla legge</B>.
                    </BulletItem>

                </View>
                {F(1)}
            </Page>

            {/* Pagina 2 */}
            <Page size="A4" style={pageStyle}>
                {H()}
                <View style={contentStyle}>

                    <SectionTitle>CONFERIMENTO DELL'INCARICO</SectionTitle>

                    <Para>
                        Tutto ciò premesso, il Mandante <B>conferisce incarico</B> a <B>{nomeAzienda}</B>, di seguito denominata <B>"Agenzia"</B>, affinché svolga le attività tecniche di bonifica meglio descritte nel presente mandato, nei limiti consentiti dalla legge, dalle autorizzazioni possedute dall'Agenzia e dalle finalità lecite dichiarate dal Mandante.
                    </Para>

                    <SectionTitle>OGGETTO DELL'INTERVENTO E BENI INTERESSATI</SectionTitle>

                    <Para bold>L'Agenzia è incaricata di eseguire le attività di bonifica presso i seguenti luoghi, beni, veicoli e/o dispositivi:</Para>
                    <Para style={{ fontSize: 8, color: C.midGray, fontStyle: 'italic', marginBottom: 4 }}>
                        (indicare tipologia, indirizzo, locali interessati, modalità di accesso; in caso di automezzo indicare la targa)
                    </Para>
                    <View style={styles.textBox}>
                        <Text style={styles.textBoxContent}>{pratica?.oggetto_attivita || ''}</Text>
                    </View>
                    <Para style={{ fontSize: 8.5, fontStyle: 'italic', color: C.midGray }}>
                        I luoghi, i beni e i dispositivi oggetto dell'intervento potranno essere specificati, integrati o meglio individuati anche nel corso delle attività, su espressa richiesta del Mandante.
                    </Para>

                    {/* Data inizio / termine */}
                    <View style={[styles.fieldRow, { marginTop: 6 }]}>
                        <Text style={[styles.fieldLabelBold, { width: 200 }]}>Data e Ora - Inizio servizio:</Text>
                        <Text style={styles.fieldValue}>
                            {pratica?.data_inizio_servizio
                                ? `${pratica.data_inizio_servizio}${pratica.orario_inizio_servizio ? `  –  Ore ${pratica.orario_inizio_servizio}` : ''}`
                                : ''}
                        </Text>
                    </View>
                    <View style={[styles.fieldRow, { marginTop: 2 }]}>
                        <Text style={[styles.fieldLabelBold, { width: 200 }]}>Data e Ora - Termine servizio:</Text>
                        <Text style={styles.fieldValue}>{pratica?.termine_servizio || ''}</Text>
                    </View>

                    <SectionTitle>LEGITTIMA DISPONIBILITÀ, AUTORIZZAZIONE E MANLEVA</SectionTitle>

                    <Para>
                        Ai fini del presente mandato, per <B>"beni oggetto dell'intervento"</B> si intendono i luoghi, immobili, locali, pertinenze, veicoli, mezzi di trasporto, dispositivi elettronici, informatici, digitali e ogni altro bene indicato o successivamente specificato nel presente incarico.
                    </Para>
                    <Para>
                        Il Mandante <B>dichiara e garantisce</B>, sotto la propria <B>esclusiva responsabilità civile e penale</B>, di avere la <B>piena e legittima disponibilità giuridica e materiale</B> dei beni oggetto dell'intervento, a qualsiasi titolo idoneo, quale, a titolo esemplificativo e non esaustivo, proprietà, locazione, comodato, usufrutto, detenzione qualificata, utilizzo autorizzato, rappresentanza legale, delega, procura o altro valido titolo giuridico.
                    </Para>
                    <Para>
                        Il Mandante dichiara, altresì, che, per quanto a propria conoscenza, i beni oggetto dell'intervento <B>non sono sottoposti a sequestro, vincolo giudiziario, amministrativo, misura cautelare, provvedimento interdittivo</B> o altra limitazione che possa impedire o rendere illegittimo lo svolgimento dell'attività richiesta.
                    </Para>
                    <Para>
                        Il Mandante <B>autorizza espressamente l'Agenzia</B>, i suoi operatori tecnici, collaboratori e consulenti incaricati ad accedere ai beni oggetto dell'intervento e a svolgere le attività tecniche di bonifica previste dal presente mandato, esclusivamente nei limiti dell'incarico conferito e per le finalità dichiarate.
                    </Para>
                    <Para>
                        Il Mandante <B>manleva e tiene indenne l'Agenzia</B> da qualsiasi conseguenza, contestazione, pretesa, richiesta risarcitoria o azione di terzi derivante da dichiarazioni false, inesatte, incomplete o reticenti, nonché da eventuali difetti di legittimazione, disponibilità, autorizzazione o titolo giuridico relativi ai beni oggetto dell'intervento.
                    </Para>

                </View>
                {F(2)}
            </Page>

            {/* Pagina 3 */}
            <Page size="A4" style={pageStyle}>
                {H()}
                <View style={contentStyle}>
                    <SectionTitle>MODALITÀ TECNICHE DELL'INTERVENTO E RESPONSABILITÀ</SectionTitle>

                    <Para>
                        Il Mandante <B>dichiara di essere stato informato</B> circa la natura tecnica delle attività richieste e circa la possibile necessità di effettuare operazioni materiali, accessorie o strumentali al corretto svolgimento dell'intervento di bonifica.
                    </Para>
                    <Para>
                        Tali operazioni potranno comprendere, a titolo esemplificativo e non esaustivo, <B>controlli tecnici, ispezioni visive e strumentali, verifiche su locali, arredi, apparati, canalizzazioni, pannellature, controsoffitti, componenti impiantistici, dispositivi elettronici, informatici o digitali</B>, nonché eventuali <B>aperture, smontaggi o rimozioni non distruttive</B>, ove tecnicamente necessarie e previamente autorizzate dal Mandante.
                    </Para>
                    <Para>
                        Il Mandante <B>si assume ogni responsabilità civile, penale e patrimoniale</B> correlata alle attività richieste e autorizzate, nonché alle conseguenze derivanti da indicazioni, autorizzazioni o dichiarazioni dallo stesso rese all'Agenzia, salvo il caso di <B>dolo o colpa grave</B> imputabile all'Agenzia o ai suoi operatori.
                    </Para>

                    <Para>
                        Resta inteso che eventuali attività che comportino <B>interventi invasivi, smontaggi, rimozioni o modifiche materiali</B> su beni, impianti, strutture o dispositivi dovranno essere previamente autorizzate dal Mandante, che dichiara sin d'ora di averne titolo e legittima disponibilità.
                    </Para>

                    <SectionTitle>RINVENIMENTO DI DISPOSITIVI NON AUTORIZZATI</SectionTitle>

                    <Para>
                        In caso di <B>rinvenimento di dispositivi, apparati o sistemi non autorizzati di spionaggio elettronico</B>, siano essi di natura audio, video, audio/video, radiotrasmittente, informatica, digitale, telematica o comunque idonei alla captazione, registrazione, trasmissione, localizzazione o sottrazione di dati e informazioni, <B>l'attività dell'Agenzia si limiterà esclusivamente alla loro individuazione, identificazione ed eventuale classificazione tecnica</B>.
                    </Para>
                    <Para bold>
                        È espressamente esclusa qualsiasi attività di rimozione, apertura, disattivazione, alterazione, manomissione, custodia o gestione materiale dei dispositivi eventualmente rinvenuti da parte dell'Agenzia, dei suoi operatori tecnici, collaboratori o consulenti.
                    </Para>
                    <Para>
                        La <B>rimozione, la custodia, la conservazione e la successiva gestione dei dispositivi</B> eventualmente rinvenuti resteranno a <B>esclusivo carico del Mandante</B>, che vi provvederà autonomamente e <B>non in presenza degli operatori dell'Agenzia</B>, anche al fine di consentirne l'eventuale consegna all'Autorità Giudiziaria territorialmente competente, quali allegati a esposto, denuncia e/o querela contro ignoti, unitamente agli esiti dell'attività di bonifica.
                    </Para>
                    <Para>
                        Il Mandante <B>manleva sin d'ora l'Agenzia</B> da ogni responsabilità connessa alla rimozione, conservazione, utilizzo, alterazione, dispersione, danneggiamento, mancata consegna o destinazione dei dispositivi rinvenuti, nonché da qualsiasi conseguenza derivante da iniziative assunte dal Mandante stesso successivamente al rinvenimento.
                    </Para>

                    <SectionTitle>DICHIARAZIONI DEL MANDANTE</SectionTitle>

                    <Para>
                        Ai fini del presente mandato, il Mandante dichiara che <B>tutte le informazioni e dichiarazioni rese nel presente atto sono veritiere, complete e non reticenti</B>, rese sotto la propria <B>esclusiva responsabilità civile e penale</B>, consapevole che, in caso di dichiarazioni false, mendaci, inesatte, incomplete o reticenti, potranno derivarne le conseguenze previste dalla legge, anche ai sensi del <B>D.P.R. 28 dicembre 2000 n. 445</B>.
                    </Para>

                    <SubSectionTitle>Assenza di impedimenti conosciuti</SubSectionTitle>

                    <BulletItem>
                        di non essere a conoscenza di procedimenti, indagini, accertamenti, iscrizioni nel registro degli indagati, informazioni di garanzia, perquisizioni, ispezioni, sequestri o altri atti dell'Autorità Giudiziaria o di Polizia Giudiziaria comunque collegati ai beni oggetto dell'intervento o alle finalità del presente mandato;
                    </BulletItem>
                    <BulletItem>
                        di non trovarsi, per quanto a propria conoscenza, in condizioni, procedimenti, provvedimenti o circostanze personali, professionali, societarie o patrimoniali tali da rendere illecito, illegittimo o strumentale a finalità non consentite l'intervento richiesto all'Agenzia;
                    </BulletItem>
                    <BulletItem>
                        di impegnarsi a comunicare tempestivamente all'Agenzia qualsiasi variazione, circostanza sopravvenuta o informazione rilevante che possa incidere sulla legittimità, opportunità o corretta esecuzione dell'attività richiesta;
                    </BulletItem>


                </View>
                {F(3)}
            </Page>

            {/* Pagina 4 */}
            <Page size="A4" style={pageStyle}>
                {H()}
                <View style={contentStyle}>
                    <BulletItem>
                        di non aver ricevuto negli ultimi 18 mesi dalla presente <B>informazioni di garanzia</B> da parte dell'Autorità Giudiziaria e di non essere a conoscenza che nei propri confronti siano in atto attività di polizia giudiziaria;
                    </BulletItem>
                    <BulletItem>
                        di non aver ricevuto negli ultimi 18 mesi dalla presente <B>perquisizioni/ispezioni</B> da parte delle FF.OO.;
                    </BulletItem>
                    <BulletItem>
                        di <B>non avere carichi pendenti e precedenti penali</B>.
                    </BulletItem>

                    <SubSectionTitle>Incarichi conferiti per conto di società, aziende o enti</SubSectionTitle>

                    <BulletItem>
                        qualora il presente incarico sia conferito in nome e per conto di società, azienda, ente, associazione o altro soggetto giuridico, di <B>agire in qualità di titolare, legale rappresentante, procuratore, delegato</B> o soggetto comunque munito dei necessari poteri per il conferimento del presente mandato;
                    </BulletItem>
                    <BulletItem>
                        qualora il presente incarico sia conferito per conto di società, azienda o ente, che il soggetto rappresentato <B>non è</B>, per quanto a propria conoscenza, destinatario di <B>interdittive antimafia, misure di prevenzione, sequestri, amministrazione giudiziaria, provvedimenti interdittivi</B>, procedimenti o contestazioni rilevanti ai sensi del <B>D.Lgs. 8 giugno 2001 n. 231</B> e della <B>legge 16 marzo 2006, n. 146</B>, o di altre situazioni ostative che possano incidere sulla legittimità dell'incarico conferito.
                    </BulletItem>

                    <SubSectionTitle>Natura lecita dell'attività richiesta</SubSectionTitle>

                    <BulletItem>
                        di prendere atto che l'attività oggetto del presente incarico ha <B>natura tecnica, consulenziale e di preventiva tutela difensiva</B>, essendo finalizzata alla ricerca, individuazione, identificazione ed eventuale classificazione di dispositivi, apparati, sistemi o strumenti idonei alla captazione, intercettazione, localizzazione, registrazione, trasmissione o sottrazione di informazioni e dati;
                    </BulletItem>
                    <BulletItem>
                        di prendere atto che <B>resta espressamente escluso lo svolgimento di attività di intercettazione, captazione, controllo occulto, accesso abusivo, installazione di apparati, investigazione vietata</B> o altra attività non consentita dalla legge;
                    </BulletItem>
                    <BulletItem>
                        di prendere atto che l'attività sarà svolta esclusivamente nei limiti consentiti dalla normativa vigente, dalle autorizzazioni possedute dall'Agenzia e dalle finalità lecite dichiarate dal Mandante.
                    </BulletItem>

                    <SectionTitle>COMPENSO, SPESE E MODALITÀ DI PAGAMENTO</SectionTitle>

                    <View style={[styles.fieldRow, { marginTop: 4, alignItems: 'flex-end' }]}>
                        <Text style={[styles.fieldLabelBold, { width: 160 }]}>Compenso concordato</Text>
                        <Text style={[styles.fieldValue, { flex: 1 }]}>
                            {pratica?.compenso_concordato ? `€  ${pratica.compenso_concordato}` : ''}
                        </Text>
                        <Text style={[styles.fieldLabel, { marginLeft: 10 }]}>oltre IVA</Text>
                    </View>

                    <Para style={{ marginTop: 6 }}>
                        Restano a carico del Mandante le <B>spese vive sostenute e documentate a piè di lista</B>, quali, a titolo esemplificativo e non esaustivo, spese di trasferta, viaggio, vitto, alloggio, costi tecnici, strumentali, organizzativi e ogni altra spesa necessaria o utile all'esecuzione dell'incarico, purché inerente all'attività svolta.
                    </Para>
                    <Para>
                        Il Mandante prende atto e accetta che il corrispettivo è determinato in <B>conformità alle condizioni economiche pattuite</B>, al tariffario in uso presso l'Agenzia, alle attività effettivamente svolte, al tempo impiegato, alle risorse utilizzate e alla complessità tecnica dell'incarico.
                    </Para>
                    <Para>
                        Con la sottoscrizione del presente mandato, <B>il Mandante riconosce sin d'ora come dovuti all'Agenzia i compensi e le spese maturati</B> per l'esecuzione dell'incarico e si impegna al relativo pagamento secondo le condizioni economiche pattuite, il tariffario accettato e la documentazione contabile emessa dall'Agenzia.
                    </Para>
                    <Para>
                        La presente clausola costituisce, nei limiti di legge, <B>promessa di pagamento e ricognizione di debito ai sensi dell'art. 1988 del Codice Civile</B>, con riferimento alle somme che risulteranno dovute per le attività svolte in esecuzione del presente incarico.
                    </Para>
                    <Para>
                        In caso di <B>sospensione, interruzione o recesso dal mandato per causa imputabile al Mandante</B>, ovvero in caso di impossibilità di proseguire le attività per dichiarazioni non veritiere, omissioni, mancata collaborazione o comportamenti del Mandante stesso, <B>restano in ogni caso dovuti all'Agenzia i compensi e le spese maturati sino a quel momento</B>, salvo contestazioni specifiche, motivate e documentate.
                    </Para>


                </View>
                {F(4)}
            </Page>

            {/* Pagina 5 */}
            <Page size="A4" style={pageStyle}>
                {H()}
                <View style={contentStyle}>

                    <SectionTitle>PRIVACY, OBBLIGAZIONE DI MEZZI E CLAUSOLE FINALI</SectionTitle>

                    <Para>
                        Il Mandante dichiara di aver ricevuto <B>idonea informativa ai sensi degli artt. 13 e 14 del Regolamento UE 2016/679</B> e di aver preso atto delle modalità e finalità del trattamento dei dati personali connesse all'esecuzione del presente incarico, alla gestione amministrativa, contabile e fiscale del rapporto, alla conservazione della documentazione relativa all'incarico, nonché all'eventuale tutela, difesa o esercizio di diritti in sede giudiziaria e/o stragiudiziale.
                    </Para>

                    <Para>
                        Il Mandante dichiara di essere a conoscenza e di accettare che <B>l'obbligazione assunta dall'Agenzia è obbligazione di mezzi e non di risultato</B>, non potendo l'Agenzia garantire il rinvenimento di dispositivi, apparati, sistemi o strumenti di intercettazione, captazione, localizzazione, registrazione, trasmissione o sottrazione di dati e informazioni.
                    </Para>
                    <Para>
                        Il Mandante prende atto che, nei limiti consentiti dalla legge, <B>l'eventuale responsabilità dell'Agenzia resta limitata all'importo del corrispettivo effettivamente corrisposto dal Mandante</B>, salvo i casi di dolo o colpa grave.
                    </Para>
                    <Para>
                        Qualora il Titolare, gli operatori tecnici, i collaboratori o i consulenti dell'Agenzia fossero chiamati dall'Autorità Giudiziaria a <B>deporre in qualità di testimoni</B> o a rendere chiarimenti in relazione all'attività svolta, <B>gli onorari, le spese e i relativi costi saranno a carico del Mandante</B>, secondo il tariffario ufficiale dell'Agenzia, salvo diversa disposizione dell'Autorità competente.
                    </Para>
                    <Para>
                        Qualora nel corso delle attività emergano elementi in contrasto con quanto dichiarato dal Mandante, ovvero circostanze idonee a rendere l'incarico illecito, illegittimo, inopportuno o strumentale a finalità non consentite, <B>l'Agenzia potrà sospendere o risolvere il contratto</B>, fatto salvo il diritto al pagamento delle attività già svolte e al risarcimento di eventuali danni subiti.
                    </Para>
                    <Para>
                        Il Mandante <B>autorizza l'Agenzia ad avvalersi</B>, ove necessario e sotto la propria responsabilità organizzativa, di operatori tecnici, collaboratori esterni, consulenti e/o istituti corrispondenti, nel rispetto della normativa vigente, degli obblighi di riservatezza e della disciplina in materia di protezione dei dati personali.
                    </Para>
                    <Para>
                        Il Mandante <B>si assume ogni responsabilità circa la veridicità, completezza e correttezza delle affermazioni rese</B> in relazione alla titolarità, disponibilità, finalità e legittimità dell'incarico conferito.
                    </Para>

                    <SectionTitle>FORO COMPETENTE</SectionTitle>
                    <Para>
                        Il presente Contratto è governato dalla <B>legge italiana</B>. Per qualunque controversia relativa al presente mandato sarà competente il <B>Foro territorialmente competente per la sede legale di {nomeAzienda}</B>.
                    </Para>

                    <View style={styles.signatureSection}>
                        <Text style={styles.signatureLabel}>Il Mandante</Text>
                        <View style={{ marginBottom: 8, width: '60%', alignSelf: 'flex-start' }} wrap={false}>
                            <View style={styles.signatureArrowRow}>
                                <View style={styles.signatureLine}>
                                    <Text style={{ fontSize: 0.01, color: '#ffffff' }}>{'#otp_b1#'}</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                    <SectionTitle>APPROVAZIONE SPECIFICA</SectionTitle>

                    <Para>
                        Ai sensi e per gli effetti degli <B>artt. 1341 e 1342 c.c.</B>, il Mandante dichiara di approvare specificamente le clausole relative a: <B>legittima disponibilità, autorizzazione e manleva; responsabilità per interventi tecnici; rinvenimento di dispositivi non autorizzati; dichiarazioni del Mandante; compenso, spese e modalità di pagamento; sospensione, interruzione o recesso; obbligazione di mezzi e non di risultato; limitazione di responsabilità; utilizzo di operatori tecnici, collaboratori, consulenti e istituti corrispondenti; foro competente</B>.
                    </Para>

                    <View style={[styles.fieldRow, { marginTop: 4 }]}>
                        <Text style={[styles.fieldLabelBold, { width: 100 }]}>Luogo e data:</Text>
                        <Text style={styles.fieldValue}>{pratica?.luogo_data_approvazione || ''}</Text>
                    </View>

                    <View style={styles.signatureSection}>
                        <Text style={styles.signatureLabel}>Il Mandante</Text>
                        <View style={{ marginBottom: 8, width: '60%', alignSelf: 'flex-start' }} wrap={false}>
                            <View style={styles.signatureArrowRow}>
                                <View style={styles.signatureLine}>
                                    <Text style={{ fontSize: 0.01, color: '#ffffff' }}>{'#otp_b2#'}</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                </View>
                {F(5)}
            </Page>

        </Document>
    );
};

export default BonificaPDF;
